import {
  type KdsBreadcrumbProps,
  KdsBreadcrumbs,
  KdsText,
} from "@kazaar-platform/design-system";
import {
  type QueryCacheNotifyEvent,
  useQueryClient,
} from "@tanstack/react-query";
import { get } from "lodash";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { type Params, useMatches, useNavigate } from "react-router-dom";

type BreadcrumbsType = Parameters<typeof KdsBreadcrumbs>[0] & {
  className?: string;
};

type Matches = {
  id: string;
  pathname: string;
  params: Params<string>;
  data: string | undefined;
  handle: {
    crumb: (param?: string) => ReactNode;
  };
};

export const BreadcrumbInQueryCache = ({
  cacheKey,
  name,
}: {
  cacheKey: string[];
  name: string;
}) => {
  const queryClient = useQueryClient();
  const defaultValue = queryClient.getQueryCache().find({ queryKey: cacheKey });
  const [crumbText, setCrumbText] = useState(
    get(defaultValue?.state?.data, name)
  );

  const callbackQueryCacheSub = useCallback(
    (event: QueryCacheNotifyEvent) => {
      const data = event.query?.state.data;
      const target = get(data, name);

      if (target) {
        // We use setTimeout to avoid console error (Have a close look later maybe)
        setTimeout(() => setCrumbText(target), 1);
      }
    },
    [name]
  );

  useEffect(() => {
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe(callbackQueryCacheSub);

    return () => {
      unsubscribe();
    };
  }, [queryClient, callbackQueryCacheSub]);

  return <KdsText>{crumbText}</KdsText>;
};

export const ConnectedBreadcrumbs = ({
  ...props
}: Omit<BreadcrumbsType, "children" | "items">) => {
  // Define Breadcrumbs Items from Router
  const matches = useMatches();
  const navigate = useNavigate();

  const items: KdsBreadcrumbProps[] = (matches as Matches[])
    ?.filter((match) => Boolean(match.handle))
    .map((e) => ({
      id: e.id,
      // ToDo actuals crumb are nodes. Maybe we should modify all of them to return only strings or formatted object to match new KDSBreadcrumb item structure
      label: e.handle.crumb(e.data) as string,
      onClick: () => navigate(e.pathname),
    }));

  return <KdsBreadcrumbs {...props} items={items} />;
};
